import { useState, useEffect, useRef, useCallback, useMemo, memo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { Trash2, Search, Loader2, User } from "lucide-react";
import Header from "@/components/Header";
import BottomNav from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { capitalizarNome, normalizarBusca, podeEditarFicha } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import Logo from "@/components/Logo";
import FiltrosFichas from "@/components/FiltrosFichas";
import { useFichasProcessadas } from "@/hooks/useFichasProcessadas";
import { usePedidosAvulsos } from "@/hooks/usePedidosAvulsos";
import { useFiltrosFichas } from "@/hooks/useFiltrosFichas";
import { useVendedoresUnidade } from "@/hooks/useVendedoresUnidade";
import { useAuth } from "@/contexts/AuthContext";

interface ProcessingCard {
  id: string;
  timestamp: string;
  status: string;
  phone?: string;
  data?: any;
  nome_cliente?: string;
  codigo_ficha?: string;
  tipo?: string;
  vendedor_id?: string;
}

// ---- Helpers puros (escopo de módulo: referência estável entre renders) ----

const getTipoColor = (tipo?: string | null) => {
  if (!tipo) return "bg-muted text-muted-foreground";
  const tipoLower = tipo.toLowerCase();
  if (tipoLower.includes("aluguel") || tipoLower.includes("alugar")) {
    return "bg-blue-100 text-blue-700 border border-blue-200";
  } else if (tipoLower.includes("venda") || tipoLower.includes("vender")) {
    return "bg-green-100 text-green-700 border border-green-200";
  } else if (tipoLower.includes("ajuste") || tipoLower.includes("conserto")) {
    return "bg-purple-100 text-purple-700 border border-purple-200";
  } else {
    return "bg-primary/10 text-primary border border-primary/20";
  }
};

const getStatusText = (status: string) => {
  if (status === "pendente") return "Pendente";
  if (status === "erro") return "Erro";
  if (status === "ativa") return "Ativa";
  if (status === "baixa") return "Baixa";
  return status;
};

const getStatusColor = (status: string) => {
  if (status === "pendente") return "text-yellow-600 font-semibold";
  if (status === "erro") return "text-red-600 font-semibold";
  if (status === "ativa") return "text-green-600 font-semibold";
  if (status === "baixa") return "text-blue-600 font-semibold";
  return "text-muted-foreground";
};

const formatDate = (timestamp?: string) =>
  timestamp
    ? new Date(timestamp).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : "-";

const formatValor = (valor?: string | number | null) => {
  if (valor === null || valor === undefined || valor === "") return "-";
  const n = typeof valor === "number" ? valor : parseFloat(String(valor).replace(",", "."));
  if (Number.isNaN(n)) return "-";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

// ---- Lista virtualizada (window scroll) ----
// Renderiza apenas os itens visíveis, evitando acumular centenas de nós no DOM.

interface VirtualListProps<T> {
  items: T[];
  estimateSize: number;
  hasNext: boolean;
  fetchingNext: boolean;
  fetchNext: () => void;
  renderItem: (item: T) => ReactNode;
  getKey: (item: T) => string;
}

function VirtualList<T>({ items, estimateSize, hasNext, fetchingNext, fetchNext, renderItem, getKey }: VirtualListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useWindowVirtualizer({
    count: items.length,
    estimateSize: () => estimateSize,
    overscan: 8,
    scrollMargin: parentRef.current?.offsetTop ?? 0,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // Carrega a próxima página quando o último item entra na janela virtual.
  useEffect(() => {
    const last = virtualItems[virtualItems.length - 1];
    if (last && last.index >= items.length - 1 && hasNext && !fetchingNext) {
      fetchNext();
    }
  }, [virtualItems, items.length, hasNext, fetchingNext, fetchNext]);

  return (
    <div ref={parentRef}>
      <div style={{ height: `${virtualizer.getTotalSize()}px`, position: "relative", width: "100%" }}>
        {virtualItems.map(vi => (
          <div
            key={getKey(items[vi.index])}
            data-index={vi.index}
            ref={virtualizer.measureElement}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${vi.start - virtualizer.options.scrollMargin}px)`,
            }}
          >
            <div className="pb-3">{renderItem(items[vi.index])}</div>
          </div>
        ))}
      </div>
      {fetchingNext && (
        <div className="py-2 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}

// ---- Cards memoizados ----

interface FichaProcessadaCardProps {
  ficha: any;
  vendedorNome?: string;
  onClick: (id: string) => void;
}

const FichaProcessadaCard = memo(({ ficha, vendedorNome, onClick }: FichaProcessadaCardProps) => (
  <Card className="transition-all hover:shadow-md cursor-pointer" onClick={() => onClick(ficha.id)}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <p className="font-semibold text-sm truncate">{capitalizarNome(ficha.nome_cliente ?? undefined)}</p>
          <p className="text-xs text-muted-foreground truncate">Código: {ficha.codigo_ficha || "-"}</p>
          {vendedorNome && <p className="text-xs text-muted-foreground truncate">Vendedor: {vendedorNome}</p>}
        </div>
        <div className="flex-shrink-0 space-y-1 text-center">
          <p className="text-xs text-muted-foreground">{formatDate(ficha.created_at)}</p>
          <p className="text-xs font-medium">{formatValor(ficha.valor)}</p>
        </div>
        <span className={`flex-shrink-0 inline-block px-2 py-1 text-xs font-medium rounded ${getTipoColor(ficha.tipo ?? undefined)}`}>
          {ficha.tipo || "-"}
        </span>
      </div>
    </CardContent>
  </Card>
));
FichaProcessadaCard.displayName = "FichaProcessadaCard";

interface PedidoAvulsoCardProps {
  pedido: any;
  onClick: (fichaId: string) => void;
}

const PedidoAvulsoCard = memo(({ pedido, onClick }: PedidoAvulsoCardProps) => (
  <Card className="transition-all hover:shadow-md cursor-pointer" onClick={() => onClick(pedido.ficha_id)}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <p className="font-semibold text-sm truncate">{capitalizarNome(pedido.nome_cliente ?? undefined)}</p>
          <p className="text-xs text-muted-foreground truncate">Código: {pedido.codigo_ficha || "-"}</p>
          {pedido.vendedor_nome && <p className="text-xs text-muted-foreground truncate">Vendedor: {pedido.vendedor_nome}</p>}
        </div>
        <div className="flex-shrink-0 space-y-1 text-center">
          <p className="text-xs text-muted-foreground">{formatDate(pedido.created_at)}</p>
          <p className="text-xs font-medium">{formatValor(pedido.valor_total)}</p>
        </div>
        <span className={`flex-shrink-0 inline-block px-2 py-1 text-xs font-medium rounded ${pedido.pago ? "bg-green-100 text-green-700 border border-green-200" : "bg-red-100 text-red-700 border border-red-200"}`}>
          {pedido.pago ? "Pago" : "Não pago"}
        </span>
      </div>
    </CardContent>
  </Card>
));
PedidoAvulsoCard.displayName = "PedidoAvulsoCard";

interface FichaPendenteCardProps {
  card: ProcessingCard;
  vendedorNome?: string;
  podeExcluir: boolean;
  onClick: (id: string) => void;
  onDelete: (e: React.MouseEvent, id: string) => void;
}

const FichaPendenteCard = memo(({ card, vendedorNome, podeExcluir, onClick, onDelete }: FichaPendenteCardProps) => (
  <Card className="transition-all hover:shadow-md cursor-pointer" onClick={() => onClick(card.id)}>
    <CardContent className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-1 min-w-0">
          <p className="font-semibold text-sm truncate">{capitalizarNome(card.nome_cliente)}</p>
          <p className="text-xs text-muted-foreground">Código: {card.codigo_ficha || "-"}</p>
          <p className="text-xs text-muted-foreground">Data: {formatDate(card.timestamp)}</p>
          {vendedorNome && <p className="text-xs text-muted-foreground truncate">Vendedor: {vendedorNome}</p>}
          <p className={`text-xs ${getStatusColor(card.status)}`}>Status: {getStatusText(card.status)}</p>
        </div>
        <div className="flex-shrink-0 flex flex-col items-end gap-2">
          <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${getTipoColor(card.tipo)}`}>
            {card.tipo || "-"}
          </span>
          {podeExcluir && (
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={e => onDelete(e, card.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
));
FichaPendenteCard.displayName = "FichaPendenteCard";

const Fichas = () => {
  const navigate = useNavigate();
  const [cards, setCards] = useState<ProcessingCard[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>("processada");
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");

  const { data: vendedores = [] } = useVendedoresUnidade();
  const vendedorNomes = useMemo(() => new Map(vendedores.map(v => [v.id, v.nome])), [vendedores]);

  const { user, activeUnidade } = useAuth();
  const podeEditar = useCallback(
    (vendedorId?: string | null) => podeEditarFicha(activeUnidade?.role, user?.id, vendedorId),
    [activeUnidade?.role, user?.id]
  );

  const ehGlobal = ["gestor", "master"].includes(activeUnidade?.role ?? "");

  // Filtros persistentes (localStorage), compartilhados entre as abas
  const { filtros, setFiltros } = useFiltrosFichas();

  // Debounce do input de busca
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 400);
    return () => clearTimeout(t);
  }, [searchText]);

  const {
    data: processadasData,
    isLoading: processadasLoading,
    isFetchingNextPage: processadasFetchingNext,
    fetchNextPage: processadasFetchNext,
    hasNextPage: processadasHasNext,
  } = useFichasProcessadas(debouncedSearch, filtros, activeFilter === "processada");

  const fichasProcessadas = useMemo(() => processadasData?.pages.flat() ?? [], [processadasData]);

  const {
    data: avulsosData,
    isLoading: avulsosLoading,
    isFetchingNextPage: avulsosFetchingNext,
    fetchNextPage: avulsosFetchNext,
    hasNextPage: avulsosHasNext,
  } = usePedidosAvulsos(debouncedSearch, filtros, activeFilter === "avulsos");

  const pedidosAvulsos = useMemo(() => avulsosData?.pages.flat() ?? [], [avulsosData]);

  useEffect(() => {
    let mounted = true;

    const fetchPreCadastros = async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data, error } = await supabase
          .from('fichas')
          .select('*')
          .in('status', ['pendente', 'erro'])
          .order('created_at', { ascending: false })
          .range(0, 99);
        if (error) {
          console.error('Erro ao buscar pré-cadastros:', error);
          return;
        }
        if (!mounted) return;
        const mappedCards: ProcessingCard[] = data.map(item => {
          let parsedData = null;
          if (item.url_bucket && (item.url_bucket.startsWith('{') || item.url_bucket.startsWith('['))) {
            try {
              parsedData = JSON.parse(item.url_bucket);
            } catch (e) {
              console.error('Erro ao parsear url_bucket:', e);
            }
          }
          return {
            id: item.id,
            timestamp: item.created_at,
            status: item.status,
            phone: item.telefone_cliente || undefined,
            data: parsedData,
            nome_cliente: item.nome_cliente || undefined,
            codigo_ficha: item.codigo_ficha || undefined,
            tipo: item.tipo || undefined,
            vendedor_id: item.vendedor_id || undefined,
          };
        });
        setCards(mappedCards);
      } catch (error) {
        console.error('Erro ao inicializar:', error);
      }
    };

    const setupRealtime = async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const user = (await supabase.auth.getUser()).data.user;

        const channel = supabase.channel('fichas_changes').on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'fichas',
          filter: `vendedor_id=eq.${user?.id}`,
        }, payload => {
          if (!mounted) return;
          if (payload.eventType === 'INSERT') {
            const newItem = payload.new as any;
            if (!['pendente', 'erro'].includes(newItem.status)) return;
            let parsedData = null;
            if (newItem.url_bucket && (newItem.url_bucket.startsWith('{') || newItem.url_bucket.startsWith('['))) {
              try { parsedData = JSON.parse(newItem.url_bucket); } catch (e) { console.error(e); }
            }
            const newCard: ProcessingCard = {
              id: newItem.id,
              timestamp: newItem.created_at,
              status: newItem.status,
              phone: newItem.telefone_cliente || undefined,
              data: parsedData,
              nome_cliente: newItem.nome_cliente || undefined,
              codigo_ficha: newItem.codigo_ficha || undefined,
              tipo: newItem.tipo || undefined,
              vendedor_id: newItem.vendedor_id || undefined,
            };
            setCards(prev => [newCard, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            const updatedItem = payload.new as any;
            let parsedData = null;
            if (updatedItem.url_bucket && (updatedItem.url_bucket.startsWith('{') || updatedItem.url_bucket.startsWith('['))) {
              try { parsedData = JSON.parse(updatedItem.url_bucket); } catch (e) { console.error(e); }
            }
            if (!['pendente', 'erro'].includes(updatedItem.status)) {
              setCards(prev => prev.filter(card => card.id !== updatedItem.id));
              return;
            }
            setCards(prev => {
              const cardIndex = prev.findIndex(card => card.id === updatedItem.id);
              if (cardIndex >= 0) {
                return prev.map(card => card.id === updatedItem.id ? {
                  ...card,
                  status: updatedItem.status,
                  phone: updatedItem.telefone_cliente || undefined,
                  data: parsedData,
                  nome_cliente: updatedItem.nome_cliente || undefined,
                  codigo_ficha: updatedItem.codigo_ficha || undefined,
                  tipo: updatedItem.tipo || undefined,
                } : card);
              } else {
                return [{
                  id: updatedItem.id,
                  timestamp: updatedItem.created_at,
                  status: updatedItem.status,
                  phone: updatedItem.telefone_cliente || undefined,
                  data: parsedData,
                  nome_cliente: updatedItem.nome_cliente || undefined,
                  codigo_ficha: updatedItem.codigo_ficha || undefined,
                  tipo: updatedItem.tipo || undefined,
                  vendedor_id: updatedItem.vendedor_id || undefined,
                }, ...prev];
              }
            });
          } else if (payload.eventType === 'DELETE') {
            const deletedItem = payload.old as any;
            setCards(prev => prev.filter(card => card.id !== deletedItem.id));
          }
        }).subscribe();
        return channel;
      } catch (error) {
        console.error('Erro ao configurar realtime:', error);
        return null;
      }
    };

    fetchPreCadastros();
    const channelPromise = setupRealtime();
    return () => {
      mounted = false;
      channelPromise.then(async channel => {
        if (channel) {
          const { supabase } = await import("@/integrations/supabase/client");
          supabase.removeChannel(channel);
        }
      });
    };
  }, []);

  const handleCardClick = useCallback((id: string) => {
    navigate(`/editar-ficha-v3/${id}`);
  }, [navigate]);

  const handleDeleteClick = useCallback((e: React.MouseEvent, cardId: string) => {
    e.stopPropagation();
    setDeletingCardId(cardId);
  }, []);

  const handleConfirmDelete = async () => {
    if (!deletingCardId) return;
    const alvo = cards.find(c => c.id === deletingCardId);
    if (alvo && !podeEditar(alvo.vendedor_id)) {
      setDeletingCardId(null);
      return;
    }
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { error } = await supabase.from('fichas').delete().eq('id', deletingCardId);
      if (error) throw error;
      setCards(prev => prev.filter(card => card.id !== deletingCardId));
    } catch (error) {
      console.error('Erro ao deletar ficha:', error);
    } finally {
      setDeletingCardId(null);
    }
  };

  // Filtragem da aba "pendente": mescla pendente + erro (busca client-side,
  // sem case nem acento — mesmo comportamento das RPCs com sem_acento()).
  const filteredCards = useMemo(() => {
    const termo = normalizarBusca(searchText.trim());
    return cards.filter(card => {
      const statusMatch = card.status === "pendente" || card.status === "erro";
      if (!statusMatch) return false;
      if (!termo) return true;
      const nomeMatch = normalizarBusca(card.nome_cliente ?? "").includes(termo);
      const codigoMatch = normalizarBusca(card.codigo_ficha ?? "").includes(termo);
      return !!(nomeMatch || codigoMatch);
    });
  }, [cards, searchText]);

  // Contador da aba Pendente = pendente + erro
  const pendenteCount = useMemo(
    () => cards.filter(c => c.status === "pendente" || c.status === "erro").length,
    [cards]
  );

  return (
    <div className="min-h-screen bg-background flex flex-col relative">
      <Header title="Fichas" />

      <div className="fixed inset-0 flex items-center justify-center pointer-events-none opacity-5 z-0">
        <Logo className="w-96 h-96 object-contain" />
      </div>

      <main className="flex-1 p-4 pb-20 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar por código ou nome do cliente..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <Tabs value={activeFilter} onValueChange={setActiveFilter} className="mb-6">
            <TabsList className="grid w-full grid-cols-3 gap-2 h-auto p-2 bg-muted/50">
              <TabsTrigger value="pendente" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-2 data-[state=active]:border-primary/50 py-2 text-xs">
                Pendente ({pendenteCount})
              </TabsTrigger>
              <TabsTrigger value="processada" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-2 data-[state=active]:border-primary/50 py-2 text-xs">
                Processadas
              </TabsTrigger>
              <TabsTrigger value="avulsos" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-2 data-[state=active]:border-primary/50 py-2 text-xs">
                Pedidos Avulsos
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {activeFilter === "processada" ? (
            <>
              <div className="mb-4 flex items-center gap-2">
                <FiltrosFichas
                  filtros={filtros}
                  onChange={setFiltros}
                  mostrarTipo
                  mostrarUnidade={ehGlobal}
                />
                <Button
                  variant={filtros.minhas ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltros({ ...filtros, minhas: !filtros.minhas })}
                >
                  <User className="w-4 h-4 mr-1.5" />
                  Minhas fichas
                </Button>
              </div>

              {processadasLoading ? (
                <Card className="py-12">
                  <CardContent className="text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
                  </CardContent>
                </Card>
              ) : fichasProcessadas.length === 0 ? (
                <Card className="py-12">
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      {debouncedSearch ? "Nenhum resultado encontrado." : "Nenhuma ficha processada ainda."}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <VirtualList
                  items={fichasProcessadas}
                  estimateSize={104}
                  hasNext={!!processadasHasNext}
                  fetchingNext={processadasFetchingNext}
                  fetchNext={processadasFetchNext}
                  getKey={(ficha) => ficha.id}
                  renderItem={(ficha) => (
                    <FichaProcessadaCard
                      ficha={ficha}
                      vendedorNome={ficha.vendedor_id ? vendedorNomes.get(ficha.vendedor_id) : undefined}
                      onClick={handleCardClick}
                    />
                  )}
                />
              )}
            </>
          ) : activeFilter === "avulsos" ? (
            <>
              <div className="mb-4 flex items-center gap-2">
                <FiltrosFichas
                  filtros={filtros}
                  onChange={setFiltros}
                  mostrarTipo={false}
                  mostrarUnidade={ehGlobal}
                />
                <Button
                  variant={filtros.minhas ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltros({ ...filtros, minhas: !filtros.minhas })}
                >
                  <User className="w-4 h-4 mr-1.5" />
                  Minhas fichas
                </Button>
              </div>

              {avulsosLoading ? (
                <Card className="py-12">
                  <CardContent className="text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
                  </CardContent>
                </Card>
              ) : pedidosAvulsos.length === 0 ? (
                <Card className="py-12">
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      {debouncedSearch ? "Nenhum resultado encontrado." : "Nenhum pedido avulso encontrado."}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <VirtualList
                  items={pedidosAvulsos}
                  estimateSize={104}
                  hasNext={!!avulsosHasNext}
                  fetchingNext={avulsosFetchingNext}
                  fetchNext={avulsosFetchNext}
                  getKey={(pedido) => pedido.id}
                  renderItem={(pedido) => (
                    <PedidoAvulsoCard pedido={pedido} onClick={handleCardClick} />
                  )}
                />
              )}
            </>
          ) : (
            <>
              {filteredCards.length === 0 ? (
                <Card className="py-12">
                  <CardContent className="text-center">
                    <p className="text-muted-foreground">
                      {cards.length === 0 ? "Nenhuma ficha encontrada" : "Nenhuma ficha pendente"}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredCards.map(card => (
                    <FichaPendenteCard
                      key={card.id}
                      card={card}
                      vendedorNome={card.vendedor_id ? vendedorNomes.get(card.vendedor_id) : undefined}
                      podeExcluir={podeEditar(card.vendedor_id)}
                      onClick={handleCardClick}
                      onDelete={handleDeleteClick}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <AlertDialog open={!!deletingCardId} onOpenChange={open => !open && setDeletingCardId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta ficha? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BottomNav />
    </div>
  );
};

export default Fichas;
